import React, { Component } from 'react';
import styled from "@emotion/styled";
import { Inner } from 'styles/structure';
import colors from 'styles/colors';
import dimensions from 'styles/dimensions';
import Scrollspy from 'react-scrollspy';
import { document, window } from 'browser-monads';

const SubnavContainer = styled.div`
  display: block;
  position: sticky;
  width: 100%;
  top: 7.2rem;
  background-color: ${colors.yellow500};
  z-index: 100;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 2.4rem;
    height: 100%;
    background: linear-gradient(90deg, rgba(255,214,0,0) 0%, rgba(255,214,0,1) 60%);
    opacity: 1;
  }
  
  ul {
    display: block;
    white-space: nowrap;
  }
  
  li {
    display: inline-block;
    margin-right: 3.2rem;
    
    &.is-current {
      a {
        opacity: 1;
        
        &::after {
          transform: scaleX(1);
        }
      }
    }
  }
  
  a {
    position: relative;
    color: ${colors.teal800};
    font-size: 1.4rem;
    font-weight: 500;
    opacity: 0.4;
    padding: 0.4rem;
    transition: opacity 0.12s ease-in-out;
    
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0.4rem;
      width: calc(100% - 0.8rem);
      height: 0.2rem;
      background-color: ${colors.teal800};
      transform: scaleX(0);
      transform-origin: center;
      transition: transform 0.12s ease-in;
    }
    
    &:hover {
      opacity: 1;
    }
  } 
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    top: 8.8rem;
    background-color: transparent;
    
    &::after {
      display: none;
    }
    
    ul {
      display: flex;
      justify-content: space-around;
      width: 100%;
    }
    
    li {
      margin-right: 0;
      padding: 0 2rem;
    }
  }
`;

const SubnavInner = styled(Inner)`
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    overflow-x: initial;
  }
`;

const SubnavBar = styled.div`
  position: relative;
  padding: 1rem 0;
  
  &::before,
  &::after {
    content: '';
    display: block;
    width: 50vw;
    height: 100%;
    position: absolute;
    top: 0;
    background-color: ${colors.yellow500};
  }
  
  &::before {
    right: 100%;
    transform-origin: right center;
  }
  
  &::after {
    left: 100%;
    transform-origin: left center;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    background-color: ${colors.yellow500};
    padding: 2rem 0;
  }
`;

function smoothScroll(target, duration) {
  const elTarget = document.querySelector(target);
  const startPosition = window.pageYOffset || elTarget.scrollTop || document.body.scrollTop;
  const boxTop = elTarget.getBoundingClientRect().top;
  const clientTop = elTarget.clientTop || document.body.clientTop || 0;
  const endPosition = boxTop + startPosition - clientTop - 200;
  const distance = endPosition - startPosition;
  let startTime = null;

  function animation(currentTime) {
    if (startTime === null) startTime = currentTime;
    let timeElapsed = currentTime - startTime;
    const run = ease(timeElapsed, startPosition, distance, duration);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }

    window.scrollTo(0,run);
  }

  function ease(t,b,c,d) {
    t /= d / 2;

    if(t<1) return c / 2 * t * t + b;
    t--;

    return -c / 2 * (t*(t-2)-1) + b;
  }

  requestAnimationFrame(animation);
}

class Subnav extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSticky: false,
      heroHeight: 0,
    }
  }

  onScroll = () => {
    if (!this.state.isSticky && window.pageYOffset >= (this.state.heroHeight * 0.8)) {
      this.setState({
        isSticky: true,
      });
    } else if (this.state.isSticky && window.pageYOffset < (this.state.heroHeight * 0.8)) {
      this.setState({
        isSticky: false,
      });
    }
  };

  onResize = () => {
    this.setState({
      heroHeight: document.querySelector('.Hero').clientHeight,
    });
  };

  onClick = (e) => {
    e.preventDefault();

    smoothScroll(e.currentTarget.getAttribute('href'), 1000);
  };

  componentDidMount() {
    // Test via a getter in the options object to see if the passive property is accessed
    let supportsPassive = false;

    try {
      const opts = Object.defineProperty({}, 'passive', {
        get: function() {
          supportsPassive = true;

          return supportsPassive;
        }
      });

      window.addEventListener("testPassive", null, opts);
      window.removeEventListener("testPassive", null, opts);
    } catch (e) {}

    // Use our detect's results. passive applied if supported, capture will be false either way.
    document.addEventListener('scroll', this.onScroll.bind(this), supportsPassive ? { passive: true } : false);

    document.addEventListener('resize', this.onResize.bind(this));

    this.onResize();
  }

  render() {
    const self = this;
    const anchors = this.props.sections.map(section => { return section.anchor});

    return (
      <SubnavContainer className={`${self.state.isSticky ? 'is-sticky' : ''}`}>
        <SubnavInner>
          <SubnavBar>
            <Scrollspy items={ anchors } offset={-200} currentClassName="is-current">
              {this.props.sections.map((section, i) => {
                if (section.hasContent) {
                  return (
                    <li key={i}>
                      <a href={`#${section.anchor}`} onClick={(e) => this.onClick(e)}>{section.label}</a>
                    </li>
                  )
                }

                return null;
              })}
            </Scrollspy>
          </SubnavBar>
        </SubnavInner>
      </SubnavContainer>
    )
  }
}

export default Subnav;
